import type { Raster } from '@repo/warp-engine';
import { type ReactElement, useEffect, useId, useRef, useState } from 'react';
import { drawRasterToCanvas } from '../canvas-raster';
import type { FaceRenderState } from '../face-render/use-face-render';
import type { ZoomPan } from '../zoom-pan/use-zoom-pan';

interface FaceStageProps {
  /** 면 이름(앞면·뒷면…) — 무대·그림의 접근 이름에 쓴다. */
  readonly label: string;
  readonly faceRender: FaceRenderState;
  readonly zoom: ZoomPan;
}

const RETRY_BUTTON =
  'inline-flex h-11 cursor-pointer items-center justify-center rounded-full border border-input bg-background px-5 text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
/**
 * 무대는 뷰어 틀의 남는 높이를 다 쓴다(flex-1) — 창이 커지면 맞춤 그림도 커진다. 아주 낮은 창에서도 256px(min-h-64) 아래로는 줄지 않는다.
 * 위치(relative)·넘침(hidden)·끌기 커서·touch-action 은 확대 훅의 stageStyle 이 정한다.
 */
const STAGE_BASE =
  'min-h-64 flex-1 bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset';

/**
 * 그림 무대 — 고른 면의 합성 결과를 캔버스에 옮긴다(100% = 렌더 1px 당 CSS 1px). 확대·이동은 CSS transform 으로만 한다(V10).
 * 그리는 중이면 "미리보기를 만드는 중…"을 띄운다. 이 면만 실패하면 "미리보기를 만들지 못했어요"와 다시 시도를 띄운다(V12) —
 * 합성 실패면 다시 시도가 훅의 retry 로 그 면을 다시 그리고, 캔버스로 옮기기 실패(2D 컨텍스트 없음·메모리 부족 등)면 옮기기만
 * 다시 한다. 옮기기 실패는 그 결과(면)에만 붙어 다른 면은 그대로 그리고, 앱에 오류 경계가 없어도 화면 전체가 깨지지 않는다.
 * 무대는 포커스를 받는다(키보드 확대·화살표 이동의 자리 — 키는 뷰어 바깥 틀의 onKeyDown 이 받는다). 확대 훅의
 * stageRef(크기 재기·Ctrl/⌘+휠)·stageHandlers(끌기)·stageStyle 을 이 요소에 달고, 캔버스에는 contentStyle(그림 크기·배율·이동)을 단다.
 */
export const FaceStage = ({ label, faceRender, zoom }: FaceStageProps): ReactElement => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hintId = useId();
  const { status, raster, retry } = faceRender;
  const isReady = status === 'ready';
  // 캔버스로 옮기지 못한 결과 — 면마다 결과 객체가 달라 다른 면에는 실패가 붙지 않는다.
  const [undrawnRaster, setUndrawnRaster] = useState<Raster | null>(null);
  const [drawAttempt, setDrawAttempt] = useState(0);
  const drawFailed = isReady && raster !== null && raster === undrawnRaster;

  // biome-ignore lint/correctness/useExhaustiveDependencies: drawAttempt 는 다시 시도 때 같은 결과를 다시 옮기려는 신호다.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!isReady || canvas === null || raster === null) return;
    try {
      drawRasterToCanvas(canvas, raster);
      setUndrawnRaster(null);
    } catch {
      setUndrawnRaster(raster);
    }
  }, [isReady, raster, drawAttempt]);

  const onRetry = (): void => {
    if (drawFailed) setDrawAttempt((attempt) => attempt + 1);
    else retry();
  };

  return (
    <section
      ref={zoom.stageRef}
      aria-label={`${label} 미리보기 화면`}
      aria-describedby={hintId}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: 무대가 키보드(+ - 0·화살표) 확대·이동과 끌기 이동을 받는 조작 대상이다.
      tabIndex={0}
      style={zoom.stageStyle}
      {...zoom.stageHandlers}
      className={STAGE_BASE}
    >
      <p id={hintId} className="sr-only">
        + 키로 확대, - 키로 축소, 0 키로 맞춤. 확대하면 끌거나 화살표 키로 옮길 수 있어요.
      </p>
      {isReady && raster !== null ? (
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`${label} 미리보기`}
          // 옮기지 못한 캔버스는 비어 있다 — 숨기되 다시 시도가 같은 캔버스에 옮기도록 남겨 둔다.
          hidden={drawFailed}
          className="select-none"
          style={zoom.contentStyle}
        />
      ) : null}
      {status === 'failed' || drawFailed ? (
        <div
          role="alert"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center"
        >
          <p className="text-sm text-destructive">미리보기를 만들지 못했어요</p>
          <button type="button" onClick={onRetry} className={RETRY_BUTTON}>
            다시 시도
          </button>
        </div>
      ) : null}
      {status === 'idle' || status === 'rendering' ? (
        <p
          role="status"
          className="absolute inset-0 flex items-center justify-center p-6 text-sm text-muted-foreground"
        >
          미리보기를 만드는 중…
        </p>
      ) : null}
    </section>
  );
};
